<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchDirectMarket,
  createDirectMarket,
  updateDirectMarket,
} from '@apis/admin/market'
import { fetchAssets } from '@apis/admin/asset'

export default {
  props: {
    market_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      assets: [],
      loading: false,
      active_tab: 'info',
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadMarket(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      if (this.account_seq) return []

      const { data } = await fetchAssets()

      this.assets = data
    },
    async loadMarket() {
      const { market_info } = await fetchDirectMarket(this.market_seq)
      this.params = defaults(market_info, {
        source_asset_seq: null,
        target_asset_seq: null,
        basic_rate: 0,
        bid_spread_rate: 0,
        ask_spread_rate: 0,
        allow_bid: 'Y',
        allow_ask: 'Y',
        is_active: 'Y',
        regdate: null,
      })
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updateDirectMarket
        : createDirectMarket
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_direct_market_modify')
        : this.$t('admin.msg_direct_market_enroll')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    date,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.direct_market_seq') }}</div></th
              >
              <td>{{ params.seq || $t('admin.auto') }}</td>
              <th scope="row"
                ><div>{{ $t('admin.c2c_market_regdate') }}</div></th
              >
              <td>{{
                params.seq
                  ? date('Y-m-d H:i:s', new Date(params.regdate))
                  : $t('admin.before_enroll')
              }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.direct_basic_rate') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.basic_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w100, $style.tRight]"
                />
              </td>
              <th scope="row"
                ><div>{{ $t('admin.modal_is_active') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.is_active"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.transfer_source_asset_title') }}</div></th
              >
              <td>
                <ElSelect
                  v-if="!params.seq"
                  v-model="params.source_asset_seq"
                  filterable
                  placeholder="Select"
                >
                  <ElOption
                    v-for="source_asset in assets"
                    :key="source_asset.seq"
                    :label="`${source_asset.title} (${source_asset.code})`"
                    :value="source_asset.seq"
                  >
                  </ElOption>
                </ElSelect>
                <template v-else>{{ params.source_asset_title }}</template>
              </td>
              <th scope="row"
                ><div>{{ $t('admin.transfer_target_asset_title') }}</div></th
              >
              <td>
                <ElSelect
                  v-if="!params.seq"
                  v-model="params.target_asset_seq"
                  filterable
                  placeholder="Select"
                >
                  <ElOption
                    v-for="target_asset in assets"
                    :key="target_asset.seq"
                    :label="`${target_asset.title} (${target_asset.code})`"
                    :value="target_asset.seq"
                  >
                  </ElOption>
                </ElSelect>
                <template v-else>{{ params.target_asset_title }}</template>
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.direct_bid_spread_rate') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.bid_spread_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                %
              </td>
              <th scope="row"
                ><div>{{ $t('admin.direct_ask_spread_rate') }}</div></th
              >
              <td>
                <input
                  v-model.number="params.ask_spread_rate"
                  type="text"
                  :class="[$style.inputTypeText, $style.w80, $style.tRight]"
                />
                %
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.direct_allow_bid') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.allow_bid"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
              <th scope="row"
                ><div>{{ $t('admin.direct_allow_ask') }}</div></th
              >
              <td
                ><ElSwitch
                  v-model="params.allow_ask"
                  active-value="Y"
                  inactive-value="N"
                ></ElSwitch
              ></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
